import { GoogleMap } from '../google-map'
import { handleCount } from '../pages/search'
import { DOMReady } from '../lib/domready'

DOMReady(main)

/* ----------------------------------- */
/* ---------- Place Swapper ---------- */
/* ----------------------------------- */

const franceNames = {
  de: 'Frankreich',
  en: 'France',
  es: 'Francia',
  fr: 'France',
  it: 'Francia',
}

const brittanyNames = {
  de: 'Bretagne',
  en: 'Brittany',
  es: 'Bretaña',
  fr: 'Bretagne',
  it: 'Bretagna',
}

function isBrittany(place) {
  const type = getType(place)
  if (type === 'locality') {
    for (const component of place.address_components) {
      if (
        component.types.includes('locality') &&
        Object.values(brittanyNames).includes(component.long_name)
      ) {
        return true
      }
    }
  }
  return false
}

function swapBrittany() {
  return {
    address_components: [
      {
        long_name: brittanyNames[document.documentElement.lang],
        short_name: brittanyNames[document.documentElement.lang],
        types: ['administrative_area_level_1', 'political'],
      },
      {
        long_name: franceNames[document.documentElement.lang],
        short_name: 'FR',
        types: ['country', 'political'],
      },
    ],
    geometry: {
      location: {
        lat: 48.2020471,
        lng: -2.9326435,
      },
      viewport: {
        northeast: {
          lat: 48.90097222951007,
          lng: -1.015612841671873,
        },
        southwest: {
          lat: 47.27789369560307,
          lng: -5.141276522772514,
        },
      },
    },
    types: ['administrative_area_level_1', 'political'],
  }
}

const swapRegistry = [{ test: isBrittany, swap: swapBrittany }]

function swapPlace(place) {
  for (const swapper of swapRegistry) {
    if (swapper.test(place)) {
      return swapper.swap(place)
    }
  }
  return place
}

/* ----------------------------- */
/* ---------- Helpers ---------- */

/* ----------------------------- */

function getOutput(input) {
  const lat = document.querySelector(input.dataset.latSelector)
  const lon = document.querySelector(input.dataset.lonSelector)
  const rad = document.querySelector(input.dataset.radWrapperSelector)

  const type = input.dataset.typeSelector
    ? document.querySelector(input.dataset.typeSelector)
    : null

  const region = input.dataset.regionSelector
    ? document.querySelector(input.dataset.regionSelector)
    : null

  const text = input.dataset.textSelector
    ? document.querySelector(input.dataset.textSelector)
    : null

  if (!lat || !lon || !rad) {
    return null
  }

  return { lat, lon, rad, text, type, region }
}

function getType(place) {
  if (place.types) {
    if (place.types.includes('locality')) {
      return 'locality'
    }
    if (place.types.includes('postal_code')) {
      return 'zipcode'
    }
    if (place.types.includes('administrative_area_level_1')) {
      return 'aa1'
    }
    if (place.types.includes('administrative_area_level_2')) {
      return 'aa2'
    }
    if (place.types.includes('country')) {
      return 'country'
    }
  }

  return null
}

function getRegion(place, type) {
  const typeMap = {
    locality: 'locality',
    zipcode: 'postal_code',
    aa1: 'administrative_area_level_1',
    aa2: 'administrative_area_level_2',
    country: 'country',
  }

  for (const component of place.address_components) {
    if (component.types.includes(typeMap[type])) {
      return component['country' === type ? 'short_name' : 'long_name']
    }
  }

  return null
}

/* ---------------------------------- */
/* ---------- Search Boxes ---------- */

/* ---------------------------------- */

function onPlaceChange(form, autocomplete, output, lock) {
  let place = autocomplete.getPlace()
  if (!place.geometry || !place.geometry.location) {
    output.rad.classList.add('hidden')
    return
  }

  lock.locked = false

  place = swapPlace(place)
  const type = getType(place)
  if (type === 'locality') {
    const lat = place.geometry.location.lat()
    const lon = place.geometry.location.lng()
    output.type.value = ''
    output.region.value = ''
    output.lat.value = lat
    output.lon.value = lon
    output.rad.classList.remove('hidden')
  } else {
    output.type.value = type
    output.region.value = getRegion(place, type)
    output.rad.classList.add('hidden')
  }

  handleCount(form)
}

function attachAutocomplete(form, input) {
  const output = getOutput(input)
  if (!output) {
    return
  }

  const options = {
    fields: ['geometry', 'types', 'address_components'],
    types: ['(regions)'],
  }

  const invalid = document.querySelector(input.dataset.invalidSelector)
  const lock = {
    locked: false,
    text: null,
  }
  window.debugLock = lock

  input.addEventListener('focusin', (e) => {
    if (!lock.locked) {
      lock.text = e.target.value
    }
  })

  input.addEventListener('input', () => {
    lock.locked = true
  })

  input.addEventListener('focusout', (e) => {
    // we want to trigger this after place_changed
    setTimeout(() => {
      if (lock.locked) {
        e.target.value = lock.text
        e.target.classList.add('is-invalid')
        invalid.classList.add('d-block')
        invalid.classList.remove('d-none')
      } else {
        e.target.classList.remove('is-invalid')
        invalid.classList.add('d-none')
        invalid.classList.remove('d-block')
      }
    }, 100)
  })

  const autocomplete = new google.maps.places.Autocomplete(input, options)
  autocomplete.addListener('place_changed', () => {
    onPlaceChange(form, autocomplete, output, lock)
  })
}

function preventSubmit(input) {
  input.addEventListener('keydown', (e) => {
    if ('Enter' === e.key) {
      e.preventDefault()
      return false
    }
  })
}

/* ---------------------------------------- */
/* ---------- Geolocation Button ---------- */

/* ---------------------------------------- */

function attachGeolocationButton(form, input) {
  if (!('geolocation' in navigator)) {
    return
  }

  const output = getOutput(input)
  if (!output) {
    return
  }

  function error() {}

  function success(position) {
    output.lat.value = position.coords.latitude
    output.lon.value = position.coords.longitude
    output.rad.classList.remove('hidden')
    output.text.value = input.dataset.successText
    handleCount(form)
  }

  input.addEventListener('click', () => {
    navigator.geolocation.getCurrentPosition(success, error)
  })
}

/* -------------------------- */
/* ---------- Main ---------- */

/* -------------------------- */

function main() {
  const searchBoxes = document.querySelectorAll('[data-geolocation]')
  if (searchBoxes.length > 0) {
    for (const input of searchBoxes) {
      input.addEventListener(
        'focusin',
        () => {
          GoogleMap.loadGoogleMapsApi(['places']).then(() => {
            const form = input.closest('form')
            attachAutocomplete(form, input)
            preventSubmit(input)
          })
        },
        { once: true },
      )
    }
  }

  const geolocationButtons = document.querySelectorAll(
    '[data-geolocation-button]',
  )
  for (const geolocationButton of geolocationButtons) {
    const form = geolocationButton.closest('form')
    attachGeolocationButton(form, geolocationButton)
  }
}
